import React, { useRef, useState } from 'react';
import Tool, {
  type IToolAvailableInput,
  type ToolButtonComponent,
} from './Tool';
import { ApplicationView, useController } from '@aha-app/mvc';
import { DEVICE_SELECTION_ERROR_MESSAGE } from '../utils/tools';
import { isDevice } from '../objects/guards';
import ToolButtonWithLabel from '../toolbars/ToolButtonWithLabel';
import { type Instance as TippyInstance } from 'tippy.js/index';
import { upperFirst } from 'lodash';
import styles from '../styles.module.css';
import ToolButton from '../toolbars/ToolButton';
import type Wireframe from '../objects/Wireframe';
import WhiteboardController from '../controllers/WhiteboardController';
import type Device from '../objects/Device';

interface PopupProps {
  currentOrientation: string;
  setCurrentOrientation: (orientation: Device['orientation']) => void;
  selection: Wireframe | fabric.Object;
  hidePopup: () => void;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({ currentOrientation, setCurrentOrientation, selection, hidePopup }) => {
    if (!isDevice(selection)) {
      console.error(DEVICE_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const controller = useController(WhiteboardController);
    const orientations = selection.orientations();

    const setOrientation = orientation => {
      setCurrentOrientation(orientation);
      selection.set('orientation', orientation);
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    return (
      <div>
        {orientations.map(orientation => (
          <ToolButton
            active={orientation === currentOrientation}
            className={styles.stringToolButton}
            key={orientation}
            onClick={() => setOrientation(orientation)}
          >
            {upperFirst(orientation)}
          </ToolButton>
        ))}
      </div>
    );
  }
);

const OrientationButton: ToolButtonComponent = ApplicationView(
  ({ selection }) => {
    if (!isDevice(selection)) {
      console.error(DEVICE_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const tippyRef = useRef<TippyInstance>();
    const initialState = selection.orientation;
    const [orientation, setOrientation] = useState(initialState);

    return (
      <ToolButtonWithLabel
        label='Orientation'
        popup={
          <Popup
            currentOrientation={orientation}
            setCurrentOrientation={setOrientation}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tippyRef={tippyRef}
      >
        {upperFirst(orientation)}
      </ToolButtonWithLabel>
    );
  }
);

class ToolDeviceOrientation extends Tool {
  static toolName = 'Orientation';

  static available({ selection }: IToolAvailableInput) {
    return selection?.type === 'device';
  }

  static buttonComponent() {
    return OrientationButton;
  }
}

export default ToolDeviceOrientation;
